import React, { useEffect, useState } from "react";
import { Bell, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  pushSupported,
  pushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getExistingSubscription,
} from "@/lib/push";
import { isStandalone, isIOSSafari } from "@/hooks/usePwaInstall";

export function PushToggle({
  compact = false,
  title = "Push notifications",
  description = "Get instant alerts — even when the app is closed.",
}) {
  const supported = pushSupported();
  const iosNeedsInstall = isIOSSafari() && !isStandalone();
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(supported ? pushPermission() : "unsupported");

  useEffect(() => {
    if (!supported) return;
    getExistingSubscription().then((s) => setSubscribed(!!s)).catch(() => {});
  }, [supported]);

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const res = await subscribeToPush();
      if (res.ok) {
        setSubscribed(true);
        setPerm("granted");
        toast.success("Notifications on — sending you a test alert now.");
        try {
          await api.post("/push/test");
        } catch (e) {
          /* best-effort */
        }
      } else if (res.reason === "denied") {
        setPerm("denied");
        toast.error("Notifications are blocked. Enable them in your browser settings.");
      } else if (res.reason === "not_configured") {
        toast.error("Notifications aren't available right now.");
      } else if (res.reason !== "unsupported") {
        toast.message("Notifications weren't enabled.");
      }
    } catch (e) {
      toast.error("Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success("Notifications turned off.");
    } catch (e) {
      toast.error("Could not turn off notifications.");
    } finally {
      setBusy(false);
    }
  };

  if (iosNeedsInstall) {
    const hint =
      "On iPhone, add FightFund to your Home Screen first (tap Share \u2192 Add to Home Screen), then you can turn on notifications.";
    if (compact) {
      return (
        <p className="mt-3 text-xs text-ff-medium" data-testid="push-ios-hint">
          {hint}
        </p>
      );
    }
    return (
      <div className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="push-toggle-card">
        <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
          <Bell className="h-4 w-4 text-ff-blue" /> {title}
        </div>
        <p className="mt-1 text-xs text-ff-medium" data-testid="push-ios-hint">
          {hint}
        </p>
      </div>
    );
  }

  const button = (
    <button
      type="button"
      data-testid={subscribed ? "push-disable-button" : "push-enable-button"}
      onClick={subscribed ? disable : enable}
      disabled={busy || perm === "denied"}
      className={`mt-3 flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 ${
        subscribed
          ? "border-ff-green bg-[#ECFDF5] text-ff-green"
          : "border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white"
      }`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : subscribed ? (
        <BellRing className="h-4 w-4" />
      ) : (
        <Bell className="h-4 w-4" />
      )}
      {busy ? "Working\u2026" : subscribed ? "Notifications on" : "Turn on notifications"}
    </button>
  );

  const deniedHint = perm === "denied" && (
    <p className="mt-2 text-[11px] text-ff-medium" data-testid="push-denied-hint">
      Notifications are blocked in your browser settings.
    </p>
  );

  if (compact) {
    return (
      <div data-testid="push-toggle-compact">
        {button}
        {deniedHint}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="push-toggle-card">
      <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
        <Bell className="h-4 w-4 text-ff-blue" /> {title}
      </div>
      <p className="mt-0.5 text-xs text-ff-medium">{description}</p>
      {button}
      {deniedHint}
    </div>
  );
}

export default PushToggle;
